import { Link } from "react-router-dom";
import {
  Sparkles, Clock, Languages, Check, ArrowRight, Phone,
  ShieldCheck, Info, ScrollText, ArrowDown,
} from "lucide-react";
import AuthorCard from "../components/site/AuthorCard";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { AUTHOR_REF, PUBLISHER_REF, SITE, breadcrumb, graph } from "../lib/schema";

// Live URL of the interactive assessment tool (hosted separately on Replit).
// Embedded in an iframe below.  We keep the URL as a constant so admins can
// swap it out later without touching layout code.
const IOL_TOOL_URL = "https://lens-selector-suite.replit.app/";
const CANONICAL_PATH = "/iol-selector";

// Comparison rows sorted by the typical progression a Varanasi patient goes
// through — cheapest to most premium.  Copy is deliberately educational, not
// promotional; it is what actually differentiates the landing page in Google's
// eyes vs generic manufacturer PR.
// Comparison rows — kept in sync with the canonical price tiers on
// /cataract-surgery-cost-in-varanasi.  If you change a range there, update
// it here too (or move both to a shared data file if this drifts often).
const LENS_TYPES = [
  {
    name: "Standard monofocal IOL",
    priceHint: "₹18,000 – ₹25,000",
    focusHint: "Single distance (usually far)",
    goodFor:
      "Patients happy to wear reading glasses.",
    tradeoff: "Sharpest distance vision. Will need reading glasses for near work.",
  },
  {
    name: "Aspheric monofocal (premium optics)",
    priceHint: "₹25,000 – ₹35,000",
    focusHint: "Single distance, sharper contrast",
    goodFor:
      "Patients who drive at night and want cleaner contrast / less glare than a standard monofocal.",
    tradeoff: "Still single-focus — reading glasses needed.",
  },
  {
    name: "Extended monofocal (functional near-intermediate)",
    priceHint: "₹40,000 – ₹45,000",
    focusHint: "Distance + a little intermediate",
    goodFor:
      "Patients who want slightly more usable range than a standard monofocal, without paying for full EDOF.",
    tradeoff: "Reading small print still needs glasses.",
  },
  {
    name: "Toric monofocal (astigmatism correction)",
    priceHint: "₹40,000 – ₹60,000",
    focusHint: "Single distance + cylindrical correction",
    goodFor:
      "Cataract patients with corneal astigmatism > 1.5 D — the toric platform corrects the cylinder inside the eye.",
    tradeoff: "Still requires reading glasses unless combined with EDOF or trifocal optics.",
  },
  {
    name: "EDOF (Extended Depth-of-Focus)",
    priceHint: "₹50,000 – ₹1,00,000",
    focusHint: "Distance + intermediate (dashboard, computer)",
    goodFor:
      "Working professionals who use a computer / phone all day. Fewer night-vision haloes than trifocal.",
    tradeoff: "Reading small print in dim light usually still needs glasses.",
  },
  {
    name: "Trifocal / Panoptix (multifocal)",
    priceHint: "₹70,000 – ₹1,10,000 per eye",
    focusHint: "Distance + intermediate + near",
    goodFor:
      "Freedom from glasses at all distances — reading, phone, driving, TV. Best for retired patients with active social lives.",
    tradeoff:
      "Small haloes around headlights at night for the first 3-6 months (usually resolve as the brain adapts).",
  },
];

const PERSONAS = [
  {
    who: "The 62-year-old retiree in Varanasi",
    need: "Reads newspaper daily, goes to Ghat in the morning, watches TV in the evening — wants glasses-free life.",
    match: "Trifocal / Panoptix IOL (₹70,000 – ₹1,10,000 per eye)",
  },
  {
    who: "The 55-year-old professional / businessman",
    need: "Long screen hours, drives to Chandauli / Jaunpur, occasional presentations.",
    match: "EDOF IOL (₹50,000 – ₹1,00,000), + toric platform if astigmatism",
  },
  {
    who: "The 70-year-old Ayushman Bharat / PMJAY beneficiary",
    need: "Wants safe, reliable vision restoration under the government scheme.",
    match: "Ayushman Bharat covers only an IOL implant worth approx ₹3,000 — the exact option for your case is clarified at the hospital.",
  },
  {
    who: "The 45-year-old teacher with high astigmatism",
    need: "Cylindrical prescription > 1.5 D that has never been fully correctable with glasses.",
    match: "Toric monofocal (₹40,000 – ₹60,000). If she also wants near vision, discuss trifocal-toric during consult.",
  },
  {
    who: "The 68-year-old diabetic with early macular changes",
    need: "Wants better vision but also needs stable retinal monitoring.",
    match: "Aspheric monofocal (₹25,000 – ₹35,000) or EDOF — trifocal not preferred if the macula is compromised.",
  },
];

// FAQ block — the answers here also power the FAQPage JSON-LD injected below,
// which is what makes the page eligible for rich results and boosts citations
// in Perplexity / ChatGPT-search / Google AI Overviews.
const IOL_FAQS = [
  {
    q: "Which IOL is best for cataract surgery in India?",
    a: "There is no single 'best' IOL — the right lens depends on your lifestyle, hobbies, driving habits, budget and the health of the rest of your eye. A retired reader who wants glasses-free vision usually does best with a trifocal IOL; a working professional who lives on a computer usually prefers an EDOF; a patient with high astigmatism needs a toric platform. Use the interactive tool above for a personalised recommendation reviewed by Dr. Shantanu Kumar Gupta.",
  },
  {
    q: "How much does IOL lens selection affect the total cataract surgery cost in Varanasi?",
    a: "The IOL itself is typically 40–70% of the surgery bill. At Shantanu Netralaya, standard monofocal IOLs start at ₹18,000 – ₹25,000. Aspheric monofocals are ₹25,000 – ₹35,000, extended monofocals ₹40,000 – ₹45,000, toric monofocals ₹40,000 – ₹60,000, EDOF IOLs ₹50,000 – ₹1,00,000, and premium trifocal / Panoptix IOLs ₹70,000 – ₹1,10,000 per eye. Surgical fees, OT charges and pre-op investigations are the same regardless of lens choice. See our transparent pricing page for the full breakdown.",
  },
  {
    q: "Can I choose a different IOL for each eye?",
    a: "Yes — this is called 'mix-and-match' and is used deliberately for some patients (e.g. distance-dominant lens in one eye and near-dominant in the other). It requires careful biometry and counselling, and is only recommended after a proper consultation. The interactive tool will flag when mix-and-match is worth discussing.",
  },
  {
    q: "Are trifocal IOLs really glasses-free?",
    a: "Around 92–95% of patients with trifocal IOLs do not need glasses for daily activities — reading, computer, driving, TV. A small subset still uses reading glasses for very small print in dim light. Trifocal IOLs also produce small haloes around headlights at night in the first 3–6 months while the brain adapts.",
  },
  {
    q: "Do I need a toric IOL if I have astigmatism?",
    a: "You benefit from a toric platform if your corneal astigmatism is ≥ 1.5 D on biometry. Below that, standard IOL centration and limbal relaxing incisions can often manage the cylinder. Above 2.0 D, a toric IOL is strongly recommended because uncorrected astigmatism will blur vision at every distance.",
  },
  {
    q: "Is the IOL selector tool a substitute for consultation with an eye surgeon?",
    a: "No — the tool gives you a well-reasoned starting recommendation based on your lifestyle and preferences. The final choice always needs a clinical exam (biometry, corneal topography, macular OCT, endothelial count) with a qualified ophthalmologist. Book a consultation at Shantanu Netralaya to convert the recommendation into a surgical plan.",
  },
];

const IOLSelector = () => {
  usePageMeta({
    title:
      "Cataract Lens (IOL) Selection in Varanasi — Interactive Selector Tool | Dr. Shantanu Kumar Gupta",
    path: CANONICAL_PATH,
    description:
      "Choose the right cataract lens for you. A free interactive IOL selector suite by Dr. Shantanu Kumar Gupta, MS, DNB — compare Monofocal, Toric, EDOF and Trifocal IOLs based on your lifestyle, budget and eye health. 3–5 minutes.",
  });

  usePageJsonLd(
    graph([
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "IOL Selector — Cataract Lens Selection", path: CANONICAL_PATH },
      ]),
      {
        "@type": "MedicalWebPage",
        "@id": `${SITE}${CANONICAL_PATH}#page`,
        url: `${SITE}${CANONICAL_PATH}`,
        name: "Cataract Lens (IOL) Selection in Varanasi",
        description:
          "Interactive IOL selector tool — recommends the right cataract lens category based on lifestyle, budget and eye health.",
        inLanguage: "en-IN",
        author: AUTHOR_REF,
        reviewedBy: AUTHOR_REF,
        publisher: PUBLISHER_REF,
        specialty: { "@type": "MedicalSpecialty", name: "Ophthalmology" },
        about: {
          "@type": "MedicalProcedure",
          name: "Phacoemulsification cataract surgery with intraocular lens (IOL) implantation",
        },
        audience: { "@type": "PatientExperience", healthCondition: "Cataract" },
      },
      // FAQPage schema — enables rich Q&A results and dramatically lifts
      // citation rates on Perplexity / ChatGPT-search / Google AI Overviews.
      {
        "@type": "FAQPage",
        "@id": `${SITE}${CANONICAL_PATH}#faq`,
        mainEntity: IOL_FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ])
  );

  return (
    <div data-testid="iol-selector-page">
      {/* ─────────── Hero ─────────── */}
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2">
          <Sparkles size={12} /> Interactive assessment · free
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-4xl">
          How to choose the right cataract lens (IOL) in Varanasi
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">
          Cataract surgery is a once-in-a-lifetime chance to upgrade your vision. Answer a few
          lifestyle questions in the interactive selector below and get a personalised IOL
          recommendation — reviewed by <strong>Dr. Shantanu Kumar Gupta, MBBS · MS (MAMC, New
          Delhi) · DNB · FICO (Merit)</strong>, Consultant Ophthalmologist (Cataract, Cornea &amp;
          Medical Retina), Shantanu Netralaya Eye Hospital, Varanasi.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a
            href={IOL_TOOL_URL}
            target="_blank"
            rel="noopener"
            className="btn-primary text-xs inline-flex items-center gap-2"
            data-testid="iol-cta-start"
          >
            Start the assessment <ArrowRight size={13} />
          </a>
          <a href="#assessment" className="btn-outline text-xs inline-flex items-center gap-2" data-testid="iol-cta-jump">
            Learn how it works <ArrowDown size={13} />
          </a>
          <Link to="/appointment" className="btn-outline text-xs inline-flex items-center gap-2">
            <Phone size={13} /> Book a consultation instead
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
          {[
            { icon: Clock, k: "3–5 min", v: "Quick & easy" },
            { icon: ShieldCheck, k: "Personalised", v: "Matched to you" },
            { icon: ScrollText, k: "Clinical guide", v: "Reviewed by doctor" },
            { icon: Languages, k: "3 languages", v: "EN · हिन्दी · मराठी" },
          ].map(({ icon: Icon, k, v }) => (
            <div key={k} className="p-4 rounded-xl border border-border bg-background/60">
              <Icon size={16} className="text-[hsl(var(--accent))]" />
              <div className="mt-2 text-sm font-medium">{k}</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────── Launch card (opens tool in new tab) ─────────── */}
      {/* We intentionally do NOT iframe the tool here. Modern browsers
          partition localStorage per top-level site, so any lens-inventory
          edits the clinic makes inside the tool's own admin editor are
          invisible when the tool is iframed under drshantanugupta.com.
          Opening in a new tab keeps the tool on its own origin and always
          reflects the latest inventory + generates a correct printable
          report. */}
      <section id="assessment" className="container-page pb-20 scroll-mt-20">
        <div className="rounded-3xl overflow-hidden border border-[hsl(var(--primary))]/25 bg-gradient-to-br from-[hsl(var(--primary))]/8 via-background to-[hsl(var(--accent))]/8 shadow-sm">
          <div className="p-8 md:p-12 grid md:grid-cols-[1fr_auto] gap-8 items-center">
            <div className="min-w-0">
              <div className="overline mb-4 inline-flex items-center gap-2">
                <Sparkles size={12} className="text-[hsl(var(--accent))]" />
                Launch the interactive assessment
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-balance">
                Get your personalised IOL recommendation now
              </h2>
              <p className="mt-4 text-sm text-muted-foreground max-w-xl leading-relaxed">
                The tool opens in a new window with the <strong>latest lens inventory
                curated by our clinic</strong>. Answer 5–7 quick lifestyle questions and
                you'll get a printable report showing the exact IOL categories that fit
                you — with prices from Shantanu Netralaya.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href={IOL_TOOL_URL}
                  target="_blank"
                  rel="noopener"
                  className="btn-primary text-xs inline-flex items-center gap-2"
                  data-testid="iol-launch-btn"
                >
                  Open IOL Assessment
                  <ArrowRight size={13} />
                </a>
                <Link
                  to="/appointment"
                  className="btn-outline text-xs inline-flex items-center gap-2"
                >
                  <Phone size={12} /> Book a consultation
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Clock size={11} /> 3–5 min</span>
                <span className="inline-flex items-center gap-1.5"><Languages size={11} /> EN · हिन्दी · मराठी</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={11} /> Printable PDF report</span>
              </div>
            </div>
            <div className="hidden md:flex items-center justify-center p-6 rounded-2xl bg-[hsl(var(--primary))]/8 border border-[hsl(var(--primary))]/15 min-w-[220px]">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">Assessment powered by</div>
                <div className="mt-2 font-serif text-lg">Shantanu Netralaya</div>
                <div className="text-xs text-muted-foreground mt-1">IOL Selection Assistant</div>
                <div className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Opens in a new tab <ArrowRight size={10} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Comparison table ─────────── */}
      <section className="container-page pb-16">
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-balance max-w-3xl">
          Types of IOL — a plain-English comparison
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Every IOL trades off in a slightly different way. Understanding the categories helps you
          have a better conversation with your surgeon.
        </p>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm min-w-[720px]" data-testid="iol-comparison-table">
            <thead className="bg-[hsl(var(--secondary))]/40 text-left">
              <tr>
                <th className="p-4 font-medium">Lens</th>
                <th className="p-4 font-medium">Price range (per eye)</th>
                <th className="p-4 font-medium">Focus provided</th>
                <th className="p-4 font-medium">Best for</th>
                <th className="p-4 font-medium">Trade-off</th>
              </tr>
            </thead>
            <tbody>
              {LENS_TYPES.map((l, i) => (
                <tr key={l.name} className={i % 2 ? "bg-[hsl(var(--secondary))]/20" : ""}>
                  <td className="p-4 font-medium align-top w-[180px]">{l.name}</td>
                  <td className="p-4 align-top w-[160px] whitespace-nowrap">{l.priceHint}</td>
                  <td className="p-4 align-top">{l.focusHint}</td>
                  <td className="p-4 align-top text-muted-foreground">{l.goodFor}</td>
                  <td className="p-4 align-top text-muted-foreground">{l.tradeoff}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Price ranges are indicative and vary by IOL model, brand and insurance package. See our{" "}
          <Link to="/cataract-surgery-cost-in-varanasi" className="underline underline-offset-4">
            transparent pricing page
          </Link>{" "}
          for the full breakdown.
        </p>
      </section>

      {/* ─────────── Personas ─────────── */}
      <section className="container-page pb-16">
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-balance max-w-3xl">
          Which lens is right for you?
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Real starting-point recommendations we give patients in our OPD — always finalised after a
          clinical exam.
        </p>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {PERSONAS.map((p) => (
            <div key={p.who} className="p-6 rounded-2xl border border-border bg-background" data-testid="iol-persona-card">
              <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))]">Patient profile</div>
              <div className="mt-2 font-serif text-lg leading-tight">{p.who}</div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.need}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-[hsl(var(--primary))]">
                <Check size={14} /> <span className="font-medium">Usually best fit:</span> {p.match}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────── FAQ ─────────── */}
      <section className="container-page pb-16">
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-balance max-w-3xl">
          Frequently asked questions
        </h2>
        <div className="mt-6 divide-y divide-border rounded-2xl border border-border overflow-hidden">
          {IOL_FAQS.map((f) => (
            <details
              key={f.q}
              className="group open:bg-[hsl(var(--secondary))]/20"
              data-testid={`iol-faq-item`}
            >
              <summary className="cursor-pointer list-none p-5 flex items-start gap-4 hover:bg-[hsl(var(--secondary))]/20">
                <Info size={14} className="mt-1 text-[hsl(var(--accent))] shrink-0" />
                <div className="flex-1 font-medium text-[15px] pr-6">{f.q}</div>
                <span className="text-[hsl(var(--accent))] text-lg leading-none select-none group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <div className="px-5 pb-5 pl-14 text-sm text-muted-foreground leading-relaxed">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* ─────────── Author + CTA ─────────── */}
      <section className="container-page pb-24 grid md:grid-cols-[1fr_320px] gap-10">
        <div>
          <h2 className="font-serif text-2xl">Ready to convert the recommendation into a plan?</h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Bring the printout of your assessment to your consultation. We will validate it against
            biometry, corneal topography and a macular OCT — and finalise the IOL that gives you the
            best real-world vision.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/appointment" className="btn-primary text-xs inline-flex items-center gap-2" data-testid="iol-cta-appointment">
              Book a consultation <ArrowRight size={13} />
            </Link>
            <Link to="/cataract-surgery-cost-in-varanasi" className="btn-outline text-xs inline-flex items-center gap-2">
              See transparent pricing
            </Link>
            <Link to="/premium-cataract-surgery-in-varanasi" className="btn-outline text-xs inline-flex items-center gap-2">
              About premium cataract surgery
            </Link>
          </div>
          <MedicalDisclaimer />
        </div>
        <aside className="self-start">
          <AuthorCard />
        </aside>
      </section>
    </div>
  );
};

export default IOLSelector;
